import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarCheck, Trophy, Sword, MapPin, Clock, GraduationCap } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

const KIND_META = {
  reta: { color: "#CCFF00", icon: Sword, label: "Reta", route: (id) => `/dashboard/retas?focus=${id}` },
  tournament: { color: "#FF6B00", icon: Trophy, label: "Torneo", route: (id) => `/dashboard/torneos/${id}` },
  league: { color: "#7C5CFF", icon: GraduationCap, label: "Liga", route: (id) => `/dashboard/ligas` },
};

function fmtDate(iso, lang) {
  if (!iso) return "Sin fecha";
  try {
    const d = new Date(iso + "T12:00:00");
    return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "es-MX", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).format(d);
  } catch { return iso; }
}

function relDate(iso, lang) {
  if (!iso) return "";
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(iso + "T12:00:00");
  const ms = d.getTime() - today.getTime();
  const days = Math.round(ms / 86400000);
  if (days === 0) return lang === "en" ? "Today" : "Hoy";
  if (days === 1) return lang === "en" ? "Tomorrow" : "Mañana";
  if (days === -1) return lang === "en" ? "Yesterday" : "Ayer";
  if (days > 0 && days < 8) return lang === "en" ? `In ${days} days` : `En ${days} días`;
  if (days < 0 && days > -8) return lang === "en" ? `${-days} days ago` : `Hace ${-days} días`;
  return "";
}

export default function Agenda() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/me/agenda");
        setData(res.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const todayISO = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    let xs = data.items || [];
    if (filter !== "all") xs = xs.filter((x) => x.kind === filter);
    if (tab === "upcoming") xs = xs.filter((x) => !x.date || x.date >= todayISO);
    else xs = xs.filter((x) => x.date && x.date < todayISO);
    return xs;
  }, [data, filter, tab, todayISO]);

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2">
          <CalendarCheck size={14} weight="fill" /> {t("nav.agenda") || "Agenda"}
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          {t("agenda.titulo") || "Mi agenda"}
        </h1>
        <p className="text-sm text-gray-400 mt-1">{t("agenda.subtitulo") || "Tus retas, torneos y ligas en orden cronológico."}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {[
          { v: "upcoming", l: t("agenda.proximos") || "Próximos" },
          { v: "past", l: t("agenda.pasados") || "Pasados" },
        ].map((x) => (
          <button
            key={x.v}
            onClick={() => setTab(x.v)}
            data-testid={`agenda-tab-${x.v}`}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold border-b-2 transition-colors ${tab === x.v ? "text-[#CCFF00] border-[#CCFF00]" : "text-gray-500 border-transparent hover:text-gray-300"}`}
          >
            {x.l}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="agenda-filters">
        {[
          { v: "all", l: t("agenda.filtro_todos") || "Todos" },
          { v: "reta", l: t("agenda.filtro_retas") || "Solo retas" },
          { v: "tournament", l: t("agenda.filtro_torneos") || "Solo torneos" },
          { v: "league", l: t("agenda.filtro_ligas") || "Solo ligas" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`agenda-filter-${f.v}`}
            className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold rounded border transition-colors ${filter === f.v ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-gray-400 border-white/20 hover:border-[#CCFF00]/40"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Items */}
      {loading && <p className="text-center text-gray-500 py-12">{t("common.cargando") || "Cargando..."}</p>}
      {!loading && filtered.length === 0 && (
        <Card className="bg-black/40 border-white/10">
          <CardContent className="p-8 text-center text-gray-500 text-sm">
            {t("agenda.sin_eventos") || "No tienes eventos en tu agenda aún"}
          </CardContent>
        </Card>
      )}
      <div className="space-y-2" data-testid="agenda-list">
        {filtered.map((it, i) => {
          const meta = KIND_META[it.kind] || KIND_META.reta;
          const Icon = meta.icon;
          const rel = relDate(it.date, lang);
          return (
            <button
              key={`${it.kind}-${it.id}-${i}`}
              onClick={() => navigate(meta.route(it.id))}
              data-testid={`agenda-item-${it.kind}-${it.id}`}
              className="w-full text-left card-tactical bg-black/40 hover:bg-black/60 border border-white/10 hover:border-[#CCFF00]/30 transition-all flex items-center gap-3 p-3"
            >
              <div className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${meta.color}1F`, border: `1px solid ${meta.color}55` }}>
                <Icon size={20} weight="fill" style={{ color: meta.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ color: meta.color }}>{meta.label}</span>
                  {it.category && <span className="text-[9px] uppercase text-gray-500">· {it.category}</span>}
                  {rel && <span className="text-[10px] text-[#CCFF00]/80">· {rel}</span>}
                </div>
                <p className="text-white font-semibold text-sm truncate">{it.title}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-0.5">
                  <span className="flex items-center gap-1"><CalendarCheck size={11} /> {fmtDate(it.date, lang) || t("agenda.sin_fecha") || "Sin fecha"}</span>
                  {it.time && <span className="flex items-center gap-1"><Clock size={11} /> {it.time}</span>}
                  {it.club_id && <span className="flex items-center gap-1 truncate"><MapPin size={11} /> {it.club_id.slice(0, 8)}</span>}
                </div>
              </div>
              <span className="shrink-0 text-[9px] uppercase tracking-wider text-gray-500">{it.role}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
